// What each /admin page looks like as a spreadsheet.
//
// ONE definition per page, used by both downloads: the button on a tab writes
// that page's sheets, and "Download everything" writes every page's sheets into
// a single workbook. They cannot drift, because there is only one description
// of what a page's data is.
//
// ── What is deliberately absent ─────────────────────────────────────────────
// The Admin Revamp (docs/ADMIN_REVAMP.md) deleted the Sign scans, Photos,
// Emails and Survey answers pages, and /api/admin-data no longer carries their
// stores — so their sheets went with them. `usage` stays as a sheet with no tab
// of its own: the per-day route counters are the durable record, and "Download
// everything" is the one file that should hold every durable record there is.
// Reports moved to Volunteer Tools. The Analytics tab has its own, finer
// download (pick one table, pick a period) in utils/analytics.js.
//
// ── Numbers stay numbers ────────────────────────────────────────────────────
// Anything countable is written as a JS number rather than a formatted string,
// so a column can be summed, averaged and charted in the spreadsheet without
// being cleaned first. That is most of the reason to hand somebody a workbook
// rather than a screenshot.

import { buildXlsx, downloadXlsx } from './xlsx.js';
import { downloadsSince } from './analytics.js';
import {
  csvTime, stamp, optionTags, destLabel, FAIL_LABELS,
  KIND_LABELS, NEARBY_KINDS,
  YEAR_LABELS, FACULTY_LABELS, COMMUTE_LABELS, platformLabel, TRIAGE_LABELS,
} from './adminLabels.js';

// ── CSV ─────────────────────────────────────────────────────────────────────

/**
 * One CSV cell.
 *
 * ⚠️ A leading `=`, `+`, `-` or `@` makes Excel and Sheets treat the cell as a
 * FORMULA, which is a real way for a value somebody typed into a web form to
 * run when the operator opens the export. Nothing reaching these files should
 * start with one — addresses are validated on the way in — but the guard costs
 * a character and the alternative is trusting every current and future column.
 * The apostrophe is the spreadsheet convention for "this is text".
 *
 * The xlsx writer needs no such guard: there a formula is its own element and
 * an inline string is inert whatever it starts with. See utils/xlsx.js.
 */
export function csvCell(v) {
  const s = v == null ? '' : String(v);
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * Hand a CSV to the browser as a download.
 *
 * The BOM is not decoration: without it Excel on Windows reads the file as the
 * local codepage and mangles any non-ASCII name in it. CRLF for the same
 * reason — it is what the format specifies and what Excel expects.
 */
export function downloadCsv(filename, header, rows) {
  const body = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([`﻿${body}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Per-page sheet definitions ──────────────────────────────────────────────
//
// Sheet names are PAGE-QUALIFIED ("Nearby by day", not "By day"). In a
// single-page download that is mild verbosity; in the everything workbook it is
// what keeps seventeen sheets apart, and a generic name would otherwise be
// deduped into "Year (2)" — which says nothing about which page it came from.

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function routeSheets(data) {
  const rows = data?.routes || [];
  return [{
    name: 'Route searches',
    // The RAW searches, one row each — not the journey grouping the tab draws.
    // A spreadsheet is where somebody counts things themselves, and `session`
    // is what lets them rebuild the grouping (or any other) with a pivot. The
    // screen's version is a reading of this; this is the record.
    // `from_node` is the nav node the search started from. For most rows it
    // only repeats what `from` says in words; for the app's outdoor
    // auto-start (`from` reads "Outdoors") it is the one column saying WHICH
    // footpath point the route began at — see isOutdoorStart().
    header: ['when', 'from', 'from_node', 'to', 'to_building', 'to_room', 'scan', 'options',
             'found', 'why_not', 'meters', 'device', 'returning', 'session'],
    rows: rows.map((r) => [
      csvTime(r.ts),
      r.from?.text || '',
      r.from?.nodeId || '',
      destLabel(r.to),
      r.to?.building || '',
      r.to?.room || '',
      r.scan || '',
      optionTags(r.options) === '—' ? '' : optionTags(r.options),
      r.found ? 'yes' : 'no',
      FAIL_LABELS[r.reason] || '',
      r.meters == null ? '' : num(r.meters),
      r.device || '',
      r.returning ? 'yes' : 'no',
      r.session || '',
    ]),
  }];
}

function usageSheets(data) {
  const days = data?.daily || [];
  return [{
    name: 'Usage by day',
    header: ['day', 'sessions', 'searches', 'routed', 'failed', 'accessible',
             'accessible_failed', 'tunnels', 'stairs_only', 'course_mode',
             'mobile_web', 'desktop', 'app_ios', 'app_android', 'returning'],
    rows: days.map((d) => [
      d.day,
      num(d.sessions), num(d.searches), num(d.found), num(d.failed),
      num(d['opt:accessible']), num(d.accessibleFailed),
      num(d['opt:tunnels']), num(d['opt:noElevators']), num(d['opt:course']),
      num(d.mobile), num(d.desktop), num(d.ios), num(d.android), num(d.returning),
    ]),
  }];
}

function nearbySheets(data) {
  const days = data?.nearby?.daily || [];
  const places = data?.nearby?.places || [];
  const sum = (f) => days.reduce((n, d) => n + (d[f] || 0), 0);
  return [
    {
      name: 'Nearby by day',
      header: ['day', 'sessions', 'events', 'panel_opens', 'place_views',
               'directions', 'no_matches'],
      rows: days.map((d) => [
        d.day, num(d.sessions), num(d.events), num(d['ev:open']),
        num(d['ev:view']), num(d['ev:go']), num(d['ev:empty']),
      ]),
    },
    {
      // Grouped by kind, most-viewed first within each — the order the tab
      // draws, so the file reads the same way the screen does.
      name: 'Nearby places',
      header: ['kind', 'place', 'views', 'directions', 'place_id'],
      rows: [...places]
        .sort((a, b) => {
          const ka = NEARBY_KINDS.indexOf(a.kind);
          const kb = NEARBY_KINDS.indexOf(b.kind);
          return (ka < 0 ? 99 : ka) - (kb < 0 ? 99 : kb)
            || String(a.kind || '').localeCompare(String(b.kind || ''))
            || (b.views || 0) - (a.views || 0);
        })
        .map((p) => [
          KIND_LABELS[p.kind] || p.kind || '', p.name || '',
          num(p.views), num(p.gos), p.placeId || '',
        ]),
    },
    {
      name: 'Nearby kinds',
      header: ['kind', 'views', 'filter_matched_nothing'],
      rows: NEARBY_KINDS.map((k) => [KIND_LABELS[k] || k, sum(`kind:${k}`), sum(`empty:${k}`)]),
    },
  ];
}

/**
 * What the club-event LAYER did, day by day.
 *
 * ⚠️ ONE SHEET, because the tab now shows one thing. The per-event sheet that
 * sat beside this went with the table it mirrored: those figures are on each
 * event's card in /eventmapcreator, where the club that made the event reads
 * its own, and `admin-data` no longer fetches them at all. A download carrying
 * a table the screen does not show is the same drift `EXPORT_PAGES` exists to
 * prevent, pointed the other way.
 *
 * `opens` is the store's own counter rather than pin + row added up here — the
 * same rule the screen follows, for the same reason: two places deriving one
 * number is two places for it to go wrong.
 */
function eventUseSheets(data) {
  const days = data?.eventUse?.daily || [];
  return [
    {
      name: 'Events by day',
      header: ['day', 'sessions', 'interactions', 'opens', 'from_pin', 'from_list',
               'directions', 'starred', 'unstarred', 'tickets', 'links', 'club_pages'],
      rows: days.map((d) => [
        d.day, num(d.sessions), num(d.events), num(d.opens),
        num(d['ev:pin']), num(d['ev:row']), num(d['ev:directions']),
        num(d['ev:save']), num(d['ev:unsave']), num(d['ev:tickets']),
        num(d['ev:link']), num(d['ev:club']),
      ]),
    },
  ];
}

function ratingSheets(data) {
  const rows = data?.routeRatings || [];
  // The last three columns are OUR side of the row, not the student's: what we
  // said about the complaint, when, and by whom. They are here rather than in a
  // second sheet because a bad rating and its triage are one record read
  // together — a spreadsheet of complaints with the answers filed separately is
  // two things to join by hand.
  return [{
    name: 'Route ratings',
    header: ['when', 'verdict', 'from', 'to', 'meters', 'options', 'accessible', 'device', 'note',
            'status', 'dev note', 'triaged'],
    rows: rows.map((r) => [
      csvTime(r.ts), r.verdict || '', r.from || '', destLabel(r.to),
      r.meters == null ? '' : num(r.meters),
      optionTags(r.options) === '—' ? '' : optionTags(r.options),
      r.options?.accessible ? 'yes' : 'no',
      r.device || '', r.note || '',
      // Blank rather than "not reviewed" for anything that is not a bad rating:
      // an ok route was never in the queue, and a column of "not reviewed"
      // beside every good one would make the export look like a backlog.
      r.verdict === 'bad' ? TRIAGE_LABELS[r.triage?.status || 'new'] : '',
      r.triage?.note || '',
      r.triage?.updatedAt ? csvTime(r.triage.updatedAt) : '',
    ]),
  }];
}

function appProfileSheets(data) {
  const c = data?.appProfileCounts || {};
  const n = (f) => num(c[f]);
  const answered = n('answers');
  const share = (v) => (answered ? Math.round((v / answered) * 1000) / 10 : '');
  const daily = data?.appProfileDaily || [];
  const rows = data?.appProfiles || [];

  const dist = (prefix, labels) => [
    ...Object.entries(labels).map(([key, label]) => [label, n(`${prefix}:${key}`), share(n(`${prefix}:${key}`))]),
    ['Skipped', n(`${prefix}:skipped`), share(n(`${prefix}:skipped`))],
  ];

  return [
    {
      // The durable line: these day hashes have no TTL, so this sheet goes back
      // further than the answers sheet below and is what a term-over-term claim
      // rests on. `answers` counts submissions, `installs` distinct phones.
      name: 'App questions by day',
      header: ['day', 'answers', 'installs', 'ios', 'android'],
      rows: daily.map((d) => [d.day, num(d.answers), num(d.installs), num(d.ios), num(d.android)]),
    },
    {
      name: 'App questions year',
      header: ['year', 'people', 'share_pct'],
      rows: dist('year', YEAR_LABELS),
    },
    {
      name: 'App questions faculty',
      header: ['faculty', 'people', 'share_pct'],
      // Biggest first: the reason to read this is to find out who is actually
      // here, and alphabetical order buries that under Agriculture.
      rows: dist('faculty', FACULTY_LABELS)
        .sort((a, b) => (a[0] === 'Skipped' ? 1 : b[0] === 'Skipped' ? -1 : b[1] - a[1])),
    },
    {
      name: 'App questions overlays',
      header: ['overlay', 'kept_on', 'turned_off'],
      rows: Object.entries(COMMUTE_LABELS)
        .map(([key, label]) => [label, n(`commute:${key}`), n(`commute-off:${key}`)]),
    },
    {
      // The raw answers behind the counts. Capped and one-year TTL, unlike the
      // by-day sheet — which is why both are here rather than either alone.
      name: 'App questions answers',
      header: ['when', 'platform', 'year', 'faculty', 'accessible', 'courses_entered', 'overlays_on'],
      rows: rows.map((r) => [
        csvTime(r.ts), platformLabel(r.platform),
        YEAR_LABELS[r.year] || (r.year || ''),
        FACULTY_LABELS[r.faculty] || (r.faculty || ''),
        r.accessible == null ? '' : (r.accessible ? 'yes' : 'no'),
        r.courses == null ? '' : num(r.courses),
        (r.commute || []).map((k) => COMMUTE_LABELS[k] || k).join(', '),
      ]),
    },
  ];
}

/**
 * Downloads of the released app, by day — see `downloadsSince`. One sheet,
 * oldest first with a running total, because the question this answers is
 * "how is the launch going" and a running total is how that gets read out.
 */
function downloadsSheets(data) {
  const d = downloadsSince(data?.appProfileDaily);
  return [{
    name: 'App downloads by day',
    header: ['day', 'downloads', 'iphone', 'android', 'running_total'],
    rows: d.rows.slice().reverse().map((r) => [r.day, r.value, r.ios, r.android, r.running]),
  }];
}

function mobileSheets(data) {
  const rows = data?.mobileSignups || [];
  const referrals = data?.referrals?.rows || [];
  const sheets = [{
    name: 'App signups',
    header: ['when', 'platform', 'student_email', 'play_store_email', 'added_as_tester'],
    rows: rows.map((r) => [
      csvTime(r.ts), platformLabel(r.platform), r.email, r.playEmail || '',
      // Blank, not "no", for iOS: that column is a Play Console job and an
      // iPhone signup does not have one. "no" would read as a to-do nobody can
      // ever tick off.
      r.platform === 'ios' ? '' : (r.invited ? 'yes' : 'no'),
    ]),
  }];
  if (referrals.length) {
    sheets.push({
      // Codes and counts, and nothing else — see api/_referrals.js. Nothing
      // here can say who a code belongs to, and nothing joins it to the sheet
      // above.
      name: 'Referral codes',
      header: ['code', 'students_referred'],
      rows: referrals.map((r) => [r.code, num(r.count)]),
    });
  }
  return sheets;
}

/**
 * Every exportable page, in the order the tabs run.
 *
 * `key` matches the dashboard's `tab` value, which is what lets the button on a
 * tab find its own definition without a second lookup table.
 */
export const EXPORT_PAGES = [
  // ⚠️ `needsAll` — the dashboard payload carries a recent SLICE of the route
  // log (see api/admin-data.js), because the whole of it is far more than one
  // response can hold. Every other page here exports what is already on screen;
  // this one has to go and fetch the rest first, or the file quietly stops at
  // the slice — which is exactly the bug this flag exists to fix. `rowCount`
  // then reports the real total, so the button promises what the file delivers.
  { key: 'routes', label: 'Route searches', file: 'routes', sheets: routeSheets,
    needsAll: true, rowCount: (d) => d?.routesTotal ?? (d?.routes || []).length },
  // No tab of its own any more (the Usage page was deleted); kept so "Download
  // everything" still carries the durable per-day route counters.
  { key: 'usage', label: 'Usage', file: 'usage', sheets: usageSheets },
  { key: 'nearby', label: 'Nearby', file: 'nearby', sheets: nearbySheets },
  { key: 'eventUse', label: 'Events', file: 'event-usage', sheets: eventUseSheets },
  { key: 'ratings', label: 'Route ratings', file: 'route-ratings', sheets: ratingSheets },
  { key: 'downloads', label: 'App downloads', file: 'app-downloads', sheets: downloadsSheets },
  { key: 'appProfile', label: 'App questions', file: 'app-questions', sheets: appProfileSheets },
  // The beta's signup list. The form that fed it left /mobile with the
  // 2026-10-05 store release; the rows are kept as the record of who tested.
  { key: 'mobile', label: 'Beta signups', file: 'beta-signups', sheets: mobileSheets },
];

/** Whether a tab has an export at all — false for In progress and Restaurants. */
export const canExport = (tabKey) => EXPORT_PAGES.some((p) => p.key === tabKey);

const pageFor = (tabKey) => EXPORT_PAGES.find((p) => p.key === tabKey);

/** How many data rows a page would export, for the button to say so. */
export function exportRowCount(tabKey, data) {
  const page = pageFor(tabKey);
  if (!page) return 0;
  try {
    if (page.rowCount) return page.rowCount(data) || 0;
    return page.sheets(data).reduce((n, s) => n + (s.rows?.length || 0), 0);
  } catch { return 0; }
}

/** Whether this page has to fetch more than the screen is holding. */
export const pageNeedsAll = (tabKey) => !!pageFor(tabKey)?.needsAll;

/**
 * Fill in the stores the dashboard only holds a slice of.
 *
 * `hydrate` is supplied by the HOST, not built here: fetching needs the admin
 * token and the API base, and neither belongs in a module about spreadsheet
 * shapes. If it is missing or fails, the export goes ahead with what is on
 * screen rather than refusing — a file of the last 5,000 searches is worth
 * having, and a download that does nothing is not.
 */
async function hydrated(page, data, hydrate) {
  if (!page.needsAll || typeof hydrate !== 'function') return data;
  try { return (await hydrate(data)) || data; } catch { return data; }
}

/** One page → its own workbook. */
export async function downloadPageXlsx(tabKey, data, hydrate) {
  const page = pageFor(tabKey);
  if (!page) return;
  const full = await hydrated(page, data, hydrate);
  downloadXlsx(`umaps-${page.file}-${stamp()}.xlsx`, page.sheets(full));
}

/**
 * One page → a CSV.
 *
 * Only the page's FIRST sheet: a CSV holds one table, and silently picking one
 * of several would produce a file whose name promises more than it carries. The
 * pages with more than one sheet say so on the button.
 */
export async function downloadPageCsv(tabKey, data, hydrate) {
  const page = pageFor(tabKey);
  if (!page) return;
  const [first] = page.sheets(await hydrated(page, data, hydrate));
  if (!first) return;
  downloadCsv(`umaps-${page.file}-${stamp()}.csv`, first.header, first.rows);
}

/** How many sheets a page produces, so the UI can be honest about the CSV. */
export function pageSheetCount(tabKey, data) {
  const page = pageFor(tabKey);
  if (!page) return 0;
  try { return page.sheets(data).length; } catch { return 0; }
}

/**
 * Every page → one workbook, one sheet per table.
 *
 * Hydrated once for the whole workbook rather than per page: "Download
 * everything" that silently held a truncated route sheet would be the worst
 * version of this bug, because it is the file somebody keeps.
 */
export async function downloadAllXlsx(data, hydrate) {
  let full = data;
  if (EXPORT_PAGES.some((p) => p.needsAll) && typeof hydrate === 'function') {
    try { full = (await hydrate(data)) || data; } catch { /* fall back to the screen's copy */ }
  }
  const sheets = EXPORT_PAGES.flatMap((p) => {
    try { return p.sheets(full); } catch { return []; }
  });
  downloadXlsx(`umaps-dashboard-${stamp()}.xlsx`, sheets);
}

/** The workbook bytes, without touching the DOM — for tests. */
export function buildAllWorkbook(data) {
  return buildXlsx(EXPORT_PAGES.flatMap((p) => p.sheets(data)));
}
